import { motion } from "framer-motion";
import { PHOTOS } from "@/data/content";

const FEATURES = [
  {
    name: "The Drew Barrymore Show",
    type: "Television — Featured Guest",
    caption: "On set with Valerie Bertinelli for the celebrated March 2026 episode",
    image: PHOTOS.drewSet,
    span: "md:col-span-8",
  },
  { name: "Goop", type: "Digital Editorial", note: "Featured expert & lecturer at Goop events across the U.S. and Canada", span: "md:col-span-4" },
  { name: "CBS", type: "Television", note: "National television feature", span: "md:col-span-4" },
  { name: "The Hollywood Reporter", type: "Press", note: "Industry press feature", span: "md:col-span-4" },
  { name: "The Art of Being Well", type: "Podcast", note: "With Dr. Will Cole", span: "md:col-span-4" },
  { name: "Oldish", type: "Podcast", note: "With Tori & Randy Spelling", span: "md:col-span-4" },
  { name: "Getting Naked", type: "Podcast", note: "With Valerie Bertinelli", span: "md:col-span-4" },
  { name: "Barstool Sports", type: "Digital Media", note: "Feature appearance", span: "md:col-span-4" },
  {
    name: "Beyond the Grave",
    type: "Documentary",
    note: "Alongside Dr. Eben Alexander — nominated at the Beverly Hills Film Festival",
    span: "md:col-span-8",
    featured: true,
  },
  { name: "Gaia — Beyond Belief", type: "Television", note: "Featured guest on Gaia's flagship series with George Noory", span: "md:col-span-4" },
  { name: "Elle", type: "Press", note: "Magazine feature", span: "md:col-span-4" },
  {
    name: "Susan × Valerie Bertinelli",
    type: "Foreword — When Shadows Speak",
    caption: "The actress and NYT bestselling author wrote the foreword to Susan's upcoming book",
    image: PHOTOS.withValerie,
    span: "md:col-span-8",
  },
];

const MEDIA_PHOTOS = [
  {
    src: PHOTOS.drewSet,
    label: "The Drew Barrymore Show",
    caption: "On set with Valerie Bertinelli — the celebrated March 2026 episode",
    testid: "media-photo-drew",
  },
  {
    src: PHOTOS.withValerie,
    label: "With Valerie Bertinelli",
    caption: "The actress and NYT bestselling author wrote the foreword to When Shadows Speak",
    testid: "media-photo-valerie",
  },
];

const OUTLETS = [
  { name: "The Drew Barrymore Show", type: "Television — Featured Guest" },
  { name: "CBS", type: "National Television" },
  { name: "Elle", type: "Print & Digital Editorial" },
  { name: "The Hollywood Reporter", type: "Industry Press" },
  { name: "Goop", type: "Editorial & Live Events" },
  { name: "Lifetime", type: "Television" },
  { name: "Gaia — Beyond Belief", type: "Television" },
  { name: "Beyond the Grave", type: "Documentary — Beverly Hills Film Festival Nominee" },
];

export default function Media() {
  return (
    <section id="media" data-testid="media-section" className="relative overflow-hidden py-28 lg:py-40">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-8 bottom-0 select-none font-display text-[16rem] font-light italic leading-none text-[#C5A059]/10"
      >
        03
      </span>

      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="font-body text-[0.65rem] font-medium uppercase tracking-[0.5em] text-[#C5A059]"
        >
          Chapter 03 — In the Media
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.76, 0, 0.24, 1] }}
          className="mt-6 max-w-4xl font-display2 uppercase leading-[0.88] tracking-[0.01em] text-[#6B5D4F] text-5xl sm:text-6xl lg:text-8xl"
        >
          The press keeps <br />
          <span className="font-display normal-case italic tracking-normal text-[#C5A059]">calling her.</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="mt-6 max-w-2xl font-body text-base font-light leading-relaxed text-[#8B7D6B]"
        >
          Thousands of interviews across television, film, print, podcasts, and radio — these are the
          stages the world already knows.
        </motion.p>

        <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2">
          {MEDIA_PHOTOS.map((p, i) => (
            <motion.figure
              key={p.label}
              data-testid={p.testid}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.12, ease: [0.76, 0, 0.24, 1] }}
              className="group"
            >
              <div className="overflow-hidden border border-[#C5A059]/40 p-2.5 transition-colors duration-500 group-hover:border-[#C5A059]">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={p.src}
                    alt={p.caption}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
              </div>
              <figcaption className="mt-5 flex flex-col gap-2 md:flex-row md:items-baseline md:justify-between md:gap-4">
                <span className="font-display text-2xl font-light italic text-[#6B5D4F]">{p.label}</span>
                <span className="font-body text-[0.6rem] font-medium uppercase tracking-[0.25em] text-[#8B7D6B] md:text-right">
                  {p.caption}
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        <div className="mt-20 border-t border-[#6B5D4F]/10">
          {OUTLETS.map((o, i) => (
            <motion.div
              key={o.name}
              data-testid={`media-row-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.6, delay: i * 0.04, ease: [0.76, 0, 0.24, 1] }}
              className="group grid grid-cols-12 items-baseline gap-4 border-b border-[#6B5D4F]/10 py-7 transition-colors duration-500 hover:bg-[#F7F1E6]"
            >
              <span className="col-span-2 pl-2 font-display text-xl italic text-[#C5A059] md:col-span-1 md:pl-4">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="col-span-10 font-display text-3xl font-light tracking-tight text-[#6B5D4F] transition-transform duration-500 group-hover:translate-x-3 md:col-span-6 md:text-5xl">
                {o.name}
              </h3>
              <p className="col-span-10 col-start-3 pr-2 font-body text-[0.65rem] font-medium uppercase tracking-[0.3em] text-[#8B7D6B] md:col-span-5 md:col-start-8 md:pr-4 md:text-right">
                {o.type}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="mx-auto mt-16 max-w-3xl text-center font-display text-2xl font-light italic leading-relaxed text-[#6B5D4F]/70 md:text-3xl"
        >
          …and thousands more interviews across television, podcasts, film, and radio —
          <span className="text-[#C5A059]"> the names above are simply the ones you already know.</span>
        </motion.p>
      </div>
    </section>
  );
}
